var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ReauthenticatableClient } from "@payleo/auth/clients";
import { JURL } from "../utils.js";
function flattenObject(obj, prefix = "") {
    const flattened = {};
    for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (value !== null && typeof value === "object" && !Array.isArray(value)) {
            Object.assign(flattened, flattenObject(value, newKey));
        }
        else {
            flattened[newKey] = value;
        }
    }
    return flattened;
}
function buildQueryParams(query) {
    const qs = new URLSearchParams();
    const flattened = flattenObject(query);
    for (const [k, v] of Object.entries(flattened)) {
        if (Array.isArray(v)) {
            for (const item of v) {
                if (v) {
                    qs.append(k, String(item));
                }
            }
        }
        else {
            if (v) {
                qs.set(k, String(v));
            }
        }
    }
    return qs;
}
export const PAYOUTS_STATUSES = [
    "queued",
    "inflight",
    "success",
    "failed",
    "errored",
    "cancelled",
];
export class PaymentsContext extends ReauthenticatableClient {
    constructor(auth, baseUrl) {
        super(auth);
        this.url = new JURL(baseUrl).joinPath("/payments");
    }
}
export function disbursement(ctx, id) {
    return {
        getPayout(reference) {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/payouts", reference), {
                    method: "GET",
                });
                return (yield res.json());
            });
        },
        start(param) {
            return __awaiter(this, void 0, void 0, function* () {
                const headers = {
                    "Idempotent-Key": param.idempotentKey,
                };
                if (param.scheduledAt) {
                    headers["Payments-Schedule-Time"] = new Date(param.scheduledAt).toISOString();
                }
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/start"), {
                    method: "POST",
                    headers,
                });
                return yield res.json();
            });
        },
        stop() {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/stop"), {
                    method: "POST",
                });
                return yield res.json();
            });
        },
        refresh() {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/payouts/refresh"), {
                    method: "POST",
                });
                return yield res.json();
            });
        },
        cancel() {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/cancel"), {
                    method: "POST",
                });
                return yield res.json();
            });
        },
        makeTransfers(tranferIds, props) {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/payouts/actions"), {
                    method: "POST",
                    body: JSON.stringify(tranferIds.map((reference) => ({ reference, action: "transfer" }))),
                    headers: {
                        "Content-Type": "application/json",
                        "Idempotent-Key": props.key,
                    },
                });
                return yield res.json();
            });
        },
        deleteTransfers(tranferIds, props) {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/payouts/actions"), {
                    method: "POST",
                    body: JSON.stringify(tranferIds.map((reference) => ({ reference, action: "delete" }))),
                    headers: {
                        "Content-Type": "application/json",
                        "Idempotent-Key": props.key,
                    },
                });
                return yield res.json();
            });
        },
        /**
         * Archives transfers: they are hidden from the default listing and are
         * never processed. The payout status becomes `cancelled`.
         */
        cancelTransfers(tranferIds, props) {
            return __awaiter(this, void 0, void 0, function* () {
                const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", id, "/payouts/actions"), {
                    method: "POST",
                    body: JSON.stringify(tranferIds.map((reference) => ({ reference, action: "cancel" }))),
                    headers: {
                        "Content-Type": "application/json",
                        "Idempotent-Key": props.key,
                    },
                });
                return yield res.json();
            });
        },
    };
}
export function deleteDisbursementRequest(ctx, companyId, id) {
    return __awaiter(this, void 0, void 0, function* () {
        const res = yield ctx.fetch(ctx.url.joinPath("/v1/company", companyId, "/disbursement", id), {
            method: "DELETE",
        });
        return yield res.json();
    });
}
export function createDisbursementRequest(ctx, companyId, params) {
    return __awaiter(this, void 0, void 0, function* () {
        const formData = new FormData();
        if (params.files) {
            for (const file of params.files) {
                formData.append("disbursements", file);
            }
        }
        if (params.description) {
            formData.append("description", params.description);
        }
        const headers = new Headers();
        if (params === null || params === void 0 ? void 0 : params.scheduledAt) {
            headers.append("Payments-Schedule-Time", new Date(params.scheduledAt).toISOString());
        }
        const res = yield ctx.fetch(ctx.url.joinPath("/v1/company", companyId, "/disbursements"), {
            method: "POST",
            body: formData,
            headers,
        });
        return (yield res.json());
    });
}
export function queryPayouts(ctx_1, companyId_1) {
    return __awaiter(this, arguments, void 0, function* (ctx, companyId, query = {}) {
        const headers = new Headers({
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
        });
        const u = ctx.url.joinPath("/v1/company", companyId, "/payouts/query");
        // set the query arguments
        const q = new URLSearchParams();
        if (typeof query.limit !== "undefined") {
            q.set("limit", query.limit.toString());
        }
        if (query.cursor) {
            q.set("cursor", query.cursor);
        }
        if (query.disbursements) {
            for (let d of query.disbursements) {
                q.append("disbursement", d);
            }
        }
        u.search = q.toString();
        let body = undefined;
        if (query.filter || query.sort) {
            const form = new URLSearchParams();
            // setting select filter
            if (query.filter) {
                for (const [key, values] of Object.entries(query.filter)) {
                    if (Array.isArray(values)) {
                        for (let v of values) {
                            form.append(key, v);
                        }
                    }
                    else {
                        form.set(key, values);
                    }
                }
            }
            // setting sortter
            if (query.sort) {
                for (const [key, values] of Object.entries(query.sort)) {
                    form.set(`sort[${key}]`, values);
                }
            }
            body = form.toString();
        }
        const res = yield ctx.fetch(u, {
            method: "POST",
            headers,
            body: body,
        });
        return (yield res.json());
    });
}
export function queryPayoutsTrend(ctx_1, companyId_1) {
    return __awaiter(this, arguments, void 0, function* (ctx, companyId, query = {}) {
        const headers = new Headers({
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
        });
        const u = ctx.url.joinPath("/v1/company", companyId, "/payouts/trend");
        // set the query arguments
        const q = new URLSearchParams();
        if (typeof query.limit !== "undefined") {
            q.set("limit", query.limit.toString());
        }
        if (query.cursor) {
            q.set("cursor", query.cursor);
        }
        if (query.disbursements) {
            for (let d of query.disbursements) {
                q.append("disbursement", d);
            }
        }
        u.search = q.toString();
        let body = undefined;
        if (query.filter || query.sort) {
            const form = new URLSearchParams();
            // setting select filter
            if (query.filter) {
                for (const [key, values] of Object.entries(query.filter)) {
                    if (Array.isArray(values)) {
                        for (let v of values) {
                            form.append(key, v);
                        }
                    }
                    else {
                        form.set(key, values);
                    }
                }
            }
            // setting sortter
            if (query.sort) {
                for (const [key, values] of Object.entries(query.sort)) {
                    form.set(`sort[${key}]`, values);
                }
            }
            body = form.toString();
        }
        const res = yield ctx.fetch(u, {
            method: "POST",
            headers,
            body: body,
        });
        return (yield res.json());
    });
}
export function getBalance(ctx, companyId) {
    return __awaiter(this, void 0, void 0, function* () {
        const res = yield ctx.fetch(ctx.url.joinPath("/v1/company", companyId, "balance"), {
            method: "GET",
        });
        return (yield res.json());
    });
}
export function previewDisbursmentRequest(ctx, companyId, input) {
    return __awaiter(this, void 0, void 0, function* () {
        const formData = new FormData();
        if (input.files) {
            for (const file of input.files) {
                formData.append("disbursements", file);
            }
        }
        const res = yield ctx.fetch(ctx.url.joinPath("/v1/company", companyId, "/disbursements/preview"), {
            method: "POST",
            body: formData,
        });
        return (yield res.json());
    });
}
export function getPaymentSchedule(ctx, scheduleId) {
    return __awaiter(this, void 0, void 0, function* () {
        const res = yield ctx.fetch(ctx.url.joinPath("/v1/disbursement", scheduleId), {
            method: "GET",
        });
        return (yield res.json());
    });
}
export function queryCompanyDisbursements(ctx_1, companyId_1) {
    return __awaiter(this, arguments, void 0, function* (ctx, companyId, query = {}) {
        let qs = new URLSearchParams();
        if (query) {
            qs = buildQueryParams(query);
        }
        const u = ctx.url.joinPath("/v1/company", companyId, "/disbursements/query");
        // set the query arguments
        const q = new URLSearchParams();
        if (typeof query.limit !== "undefined") {
            q.set("limit", query.limit.toString());
        }
        if (query.cursor) {
            q.set("cursor", query.cursor);
        }
        u.search = q.toString();
        let body = undefined;
        if (query.filter || query.sort) {
            const form = new URLSearchParams();
            // setting select filter
            if (query.filter) {
                for (const [key, values] of Object.entries(query.filter)) {
                    if (Array.isArray(values)) {
                        for (let v of values) {
                            form.append(key, v);
                        }
                    }
                    else {
                        form.set(key, values);
                    }
                }
            }
            // setting sortter
            if (query.sort) {
                for (const [key, values] of Object.entries(query.sort)) {
                    form.set(`sort[${key}]`, values);
                }
            }
            body = form.toString();
        }
        const res = yield ctx.fetch(u, {
            method: "POST",
            body: body,
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        });
        return (yield res.json());
    });
}
